/** #754/#902 adapters over existing rendered groups. No feed or trading effects. */
import type { GameEventRow, GameRow, MultiOutcomeEvent, Venue, VenueBook } from './types';
import { gameRowDiscoveryGroup, type DiscoveryGroup } from './discovery';
import { futuresRows, isPolyFutureBoard, type FuturesBoard } from './futuresDetail';
import { isSlateDate } from './weeklySlate';

export type DiscoveryRow =
  | { kind: 'moneyline'; value: GameRow }
  | { kind: 'multi'; value: MultiOutcomeEvent }
  | { kind: 'lines'; value: GameEventRow }
  | { kind: 'future'; value: FuturesBoard };

export interface DiscoveryCatalogInput {
  moneylines: readonly GameRow[];
  multi: readonly MultiOutcomeEvent[];
  lines: readonly GameEventRow[];
  futures: readonly FuturesBoard[];
}

function key(kind: DiscoveryRow['kind'], sport: string, identity: string, variant = ''): string {
  // Never manufacture a valid identity for an unidentified source row.
  return identity ? JSON.stringify([kind, sport, identity, variant]) : '';
}
function text(parts: readonly (string | number | null | undefined)[]): string {
  return parts.filter(v => v != null).join(' ').normalize('NFKC').toLowerCase();
}
function venues(books: readonly { kalshi?: VenueBook | null; polymarket?: VenueBook | null }[]): Venue[] {
  return [...(books.some(b => b.kalshi) ? ['kalshi' as const] : []),
    ...(books.some(b => b.polymarket) ? ['polymarket' as const] : [])];
}
const unsupportedVolume = () => ({ value: null, coverage: 'unsupported' as const });

/** Retain the original whole group and exact outcome/line identities. Aggregate
 * volume for multi/line/future families is deliberately not borrowed from the
 * two-team moneyline formula. Futures have no scheduled date or whole-board
 * lifecycle in their current shape; mark those absent instead of inventing them.
 */
export function buildDiscoveryCatalog(input: DiscoveryCatalogInput): DiscoveryGroup<DiscoveryRow>[] {
  return [
    ...input.moneylines.map(value => ({ ...gameRowDiscoveryGroup(value, 'moneyline'),
      key: key('moneyline', value.sport, value.key), row: { kind: 'moneyline' as const, value } })),
    ...input.multi.map((value): DiscoveryGroup<DiscoveryRow> => ({
      key: key('multi', value.sport, value.key, value.kind ?? 'winner'),
      sport: value.sport, marketType: 'moneyline', status: value.gameState,
      venues: venues(value.outcomes), startTimeMs: value.startTimeMs,
      date: isSlateDate(value.date) ? value.date : null,
      searchText: text([value.sport, value.title, value.category, value.kind ?? 'winner',
        value.awayCode, value.homeCode, value.awayName, value.homeName,
        ...value.outcomes.flatMap(o => [o.name, o.code, o.ticker, o.kalshi?.marketTicker, o.polymarket?.marketTicker])]),
      volume: unsupportedVolume(), row: { kind: 'multi', value },
    })),
    ...input.lines.map((value): DiscoveryGroup<DiscoveryRow> => ({
      key: key('lines', value.sport, value.key), sport: value.sport,
      marketType: 'spread-total', status: value.gameState,
      venues: venues([...value.spreads, ...value.totals]), startTimeMs: value.startTimeMs,
      date: isSlateDate(value.date) ? value.date : null,
      // The store's own admission day (#754), so a mode's window admits this
      // event on exactly the day the moneyline board admits the same game.
      admissionDate: value.admissionDate,
      searchText: text([value.sport, value.awayCode, value.homeCode, value.awayName, value.homeName,
        ...value.spreads.flatMap(l => ['spread', l.line, l.favored, l.kalshi?.marketTicker, l.polymarket?.marketTicker]),
        ...value.totals.flatMap(l => ['total', l.line, l.kalshi?.marketTicker, l.polymarket?.marketTicker])]),
      volume: unsupportedVolume(), row: { kind: 'lines', value },
    })),
    ...input.futures.map((value): DiscoveryGroup<DiscoveryRow> => ({
      key: key('future', value.sport, value.key, isPolyFutureBoard(value) ? 'polymarket' : 'kalshi'),
      sport: value.sport, marketType: 'series', status: null,
      venues: isPolyFutureBoard(value) ? ['polymarket'] : venues(value.outcomes),
      startTimeMs: null, date: null,
      searchText: text([value.sport, value.title,
        isPolyFutureBoard(value) ? value.tour : value.seriesTicker,
        ...futuresRows(value).flatMap(o => [o.name, o.detail, o.ticker])]),
      volume: unsupportedVolume(), row: { kind: 'future', value },
    })),
  ];
}
